import { cn } from "@babybjorn/ui";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import ConditionalWrapper from "../../components/ConditionalWrapper";
import Button from "../../components/general/Button";
import Hotspots from "../../components/general/Hotspots";
import type { HotspotProps } from "../../components/general/Hotspots/components/Hotspot/Hotspot";
import ImageContainer, {
	type VimeoResponse,
} from "../../components/general/ImageContainers/ImageContainer";
import ModalPortal from "../../components/general/ModalPortal";
import { useIsMinWidth, useIsTablet } from "../../hooks/use-is-screensize";
import useScrollBlock from "../../hooks/useScrollBlock";
import * as s from "./ProductGallery.module.scss";
import GalleryBadge from "./components/GalleryBadge";
import Indicator from "./components/Indicator/Indicator";
import SideBySide from "./components/SideBySide";

export type ProductImage = {
	alt: string;
	imgSrc: string;
	videoSrc?: string;
	youtubeEmbedId?: string;
	vimeoResponse?: VimeoResponse;
	isProductImage?: boolean;
	badge?: string;
	addPadding?: boolean;
	hotspots?: HotspotProps[];
};

type ProductGalleryProps = {
	productImage?: ProductImage;
	lookImage?: ProductImage;
	restImages: (ProductImage | ProductImage[])[];
	badge?: string;
	returnButtonLabel: string;
	ariaLabelViewAll?: string;
};

const ProductGallery: FC<ProductGalleryProps> = ({
	productImage,
	lookImage,
	restImages,
	badge,
	returnButtonLabel,
	ariaLabelViewAll,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [clickedImageIndex, setClickedImageIndex] = useState(0);
	const [, setLocked] = useScrollBlock();

	const [activeIndex, setActiveIndex] = useState(0);
	const galleryRef = useRef<HTMLDivElement>(null);

	const isTablet = useIsTablet();
	const isMobileLandscape = useIsMinWidth(479) && !isTablet;

	const modalRef = useRef<HTMLElement>(null);

	const imagesToDisplay = useMemo<(ProductImage | ProductImage[])[]>(() => {
		if (productImage && lookImage) {
			return isTablet
				? [{ ...productImage, badge }, lookImage, ...restImages]
				: [productImage, { ...lookImage, badge }, ...restImages];
		} else {
			return restImages;
		}
	}, [productImage, lookImage, isTablet, badge, restImages]);

	useEffect(() => {
		setLocked(isModalOpen);
	}, [isModalOpen, setLocked]);

	useEffect(() => {
		modalRef.current?.children[clickedImageIndex + 1]?.scrollIntoView();
	}, [clickedImageIndex]);

	const onImageClick = (index: number) => {
		setIsModalOpen(true);
		setClickedImageIndex(index);
	};

	useEffect(() => {
		const resolveActiveIndex = () => {
			const noOfImages = [productImage, lookImage, ...restImages].flat().length;

			if (galleryRef.current) {
				const { scrollLeft, scrollWidth, clientWidth } = galleryRef.current;
				const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
				const index = Math.floor(scrollPercentage * noOfImages);
				setActiveIndex(Math.min(index, noOfImages - 1));
			}
		};

		const currentGalleryRef = galleryRef.current;
		currentGalleryRef?.addEventListener("scroll", resolveActiveIndex);

		return () => {
			currentGalleryRef?.removeEventListener("scroll", resolveActiveIndex);
		};
	}, [lookImage, productImage, restImages]);

	return (
		<>
			<section className={s.productGallery}>
				<div ref={galleryRef} className={s.gallery}>
					{imagesToDisplay.length > 0 &&
						imagesToDisplay.map((data, index) => {
							return Array.isArray(data) ? (
								<ConditionalWrapper
									key={index}
									wrapper={(children) => <SideBySide>{children}</SideBySide>}
									shouldWrap={!!isTablet}
								>
									<>
										{data.map((image) => (
											<Hotspots
												key={image.imgSrc}
												name="hotpots"
												hotspots={image.hotspots}
												productGallery
											>
												<ImageContainer
													key={image.imgSrc}
													className={s.image}
													alt={image.alt}
													priority={Number(index) < 2}
													hidePlaceholder={image.addPadding}
													imgSrc={image.imgSrc}
													videoSrc={image.videoSrc}
													youtubeEmbedId={image.youtubeEmbedId}
													vimeoResponse={image.vimeoResponse}
													onClick={() => {
														onImageClick(index);
													}}
													objectFit={image.addPadding ? "contain" : "cover"}
													addPadding={image.addPadding}
													paddingVariant="gallery"
													withPlayButton={
														!!image.videoSrc ||
														!!image.youtubeEmbedId ||
														!!image.vimeoResponse
													}
													data-d="sdf"
												>
													{image.badge && (
														<GalleryBadge
															label={image.badge}
															isProductImage={image.isProductImage}
														/>
													)}
												</ImageContainer>
											</Hotspots>
										))}
									</>
								</ConditionalWrapper>
							) : (
								<Hotspots
									key={data.imgSrc}
									name="hotpots"
									hotspots={data.hotspots}
									productGallery
								>
									<ImageContainer
										key={data.imgSrc}
										className={s.image}
										alt={data.alt}
										priority={Number(index) < 2}
										hidePlaceholder={data.addPadding}
										imgSrc={data.imgSrc}
										videoSrc={data.videoSrc}
										youtubeEmbedId={data.youtubeEmbedId}
										vimeoResponse={data.vimeoResponse}
										onClick={() => {
											onImageClick(index);
										}}
										objectFit={data.addPadding ? "contain" : "cover"}
										addPadding={data.addPadding}
										paddingVariant="gallery"
										withPlayButton={
											!!data.videoSrc ||
											!!data.youtubeEmbedId ||
											!!data.vimeoResponse
										}
									>
										{data.badge && (
											<GalleryBadge
												label={data.badge}
												isProductImage={data.isProductImage}
											/>
										)}
									</ImageContainer>
								</Hotspots>
							);
						})}
					<div>
						<Button
							className={s.viewAllButton}
							label="Button"
							icon="box"
							variant="secondary"
							buttonType="standard"
							aria-label={ariaLabelViewAll}
							onClick={() => setIsModalOpen(true)}
						/>
					</div>
				</div>
				<Indicator
					amountOfImages={
						[productImage, lookImage, ...restImages].flat().length
					}
					activeIndex={activeIndex}
				/>
			</section>
			<ModalPortal show={isModalOpen}>
				<AnimatePresence>
					<motion.section
						key="modal"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						ref={modalRef}
						className={cn(s.productGalleryModal)}
					>
						<Button
							label={returnButtonLabel}
							buttonType="standard"
							icon="close"
							className={s.closeButton}
							aria-label={returnButtonLabel}
							onClick={() => {
								setIsModalOpen(false);
								setClickedImageIndex(0);
							}}
						/>
						{imagesToDisplay.map((data, index) => {
							return Array.isArray(data) ? (
								<ConditionalWrapper
									key={index}
									wrapper={(children) => <SideBySide>{children}</SideBySide>}
									shouldWrap={!isMobileLandscape}
								>
									<>
										{data.map((image) => (
											<Hotspots
												key={image.imgSrc}
												name="hotpots"
												hotspots={image.hotspots}
												productGallery
											>
												<ImageContainer
													key={image.imgSrc}
													alt={image.alt}
													ratio="portrait"
													imgSrc={image.imgSrc}
													hidePlaceholder={image.addPadding}
													videoSrc={image.videoSrc}
													youtubeEmbedId={image.youtubeEmbedId}
													vimeoResponse={image.vimeoResponse}
													style={{ overflow: "visible" }}
													objectFit={image.addPadding ? "contain" : "cover"}
													addPadding={image.addPadding}
													paddingVariant="gallery"
													withPlayButton={
														!!image.videoSrc ||
														!!image.youtubeEmbedId ||
														!!image.vimeoResponse
													}
												/>
											</Hotspots>
										))}
									</>
								</ConditionalWrapper>
							) : (
								<ImageContainer
									key={data.imgSrc}
									alt={data.alt}
									ratio="portrait"
									imgSrc={data.imgSrc}
									hidePlaceholder={data.addPadding}
									videoSrc={data.videoSrc}
									youtubeEmbedId={data.youtubeEmbedId}
									vimeoResponse={data.vimeoResponse}
									style={{ overflow: "visible" }}
									objectFit={data.addPadding ? "contain" : "cover"}
									addPadding={data.addPadding}
									paddingVariant="gallery"
									withPlayButton={
										!!data.videoSrc ||
										!!data.youtubeEmbedId ||
										!!data.vimeoResponse
									}
								/>
							);
						})}
					</motion.section>
				</AnimatePresence>
			</ModalPortal>
		</>
	);
};

export default ProductGallery;
